import {useEffect,useMemo,useState} from 'react';
import {CalendarClock,Eye,ShieldCheck,UserCheck} from 'lucide-react';
import {api,formatMoney,type PreviewView} from '../../api';
import {blockerLabel} from '../../../../src/domain/blockers/index';
import type {Currency} from '../../../../src/domain/types';
import './policy.css';
import { useAgentName } from '../../agent-name';

const message=(e:unknown)=>e instanceof Error?e.message:'This preview could not be loaded.';
const weekday=(date:string)=>new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US',{weekday:'long',month:'short',day:'numeric',timeZone:'UTC'});
const clock=(time:string)=>{const [hour,minute]=time.split(':').map(Number);const suffix=(hour??0)>=12?'PM':'AM';
 return `${(hour??0)%12||12}:${String(minute??0).padStart(2,'0')} ${suffix}`;};
const money=(amount:{currency:string;minor:string})=>formatMoney(amount.minor,amount.currency as Currency);

/** Policy preview: what the planner expects to send, day by day, without creating a draft, approval or touch. */
export function Preview({policyId}:{policyId:string}){
  const agent = useAgentName();
 const [view,setView]=useState<PreviewView|null>(null);const [error,setError]=useState('');const [loading,setLoading]=useState(true);
 const [customer,setCustomer]=useState('all');const [stage,setStage]=useState('all');
 useEffect(()=>{let live=true;setLoading(true);setError('');
  void api<PreviewView>(`/app-api/preview?days=3&policy=${encodeURIComponent(policyId)}`)
   .then(data=>{if(live)setView(data);}).catch(e=>{if(live)setError(message(e));}).finally(()=>{if(live)setLoading(false);});
  return()=>{live=false;};},[policyId]);
 const sends=useMemo(()=>view?.days.flatMap(day=>day.sends)??[],[view]);
 const customers=useMemo(()=>[...new Map(sends.map(send=>[send.customerId,send.customerName])).entries()]
  .sort((a,b)=>a[1].localeCompare(b[1])),[sends]);
 const stages=useMemo(()=>[...new Map(sends.map(send=>[send.stageId,send.stageLabel])).entries()]
  .sort((a,b)=>a[1].localeCompare(b[1])),[sends]);
 const shown=(day:PreviewView['days'][number])=>day.sends.filter(send=>(customer==='all'||send.customerId===customer)
  &&(stage==='all'||send.stageId===stage));
 if(loading)return <section className="pj-panel pv-loading" aria-busy="true"><span className="sr-only">Loading the preview</span></section>;
 if(error)return <div className="alert error" role="alert">{error}</div>;
 if(!view)return null;
 const total=view.days.reduce((sum,day)=>sum+shown(day).length,0);
 return <section className="pj-panel pv-panel" aria-labelledby="pv-title">
  <header className="pv-head"><div>
   <h2 id="pv-title"><Eye size={16} aria-hidden/> Preview: the next 3 business days</h2>
   <p className="pj-lede">Every email {agent} plans to send, with the step, the amount and who approves it. Read-only: this
    creates no draft, approval or contact.</p></div>
   <span className="pv-readonly"><ShieldCheck size={13} aria-hidden/> Nothing is sent or created</span></header>
  <div className="pv-filters" role="group" aria-label="Filter the preview">
   <label>Customer<select value={customer} onChange={e=>setCustomer(e.target.value)}>
    <option value="all">All customers</option>{customers.map(([id,name])=><option key={id} value={id}>{name}</option>)}</select></label>
   <label>Stage<select value={stage} onChange={e=>setStage(e.target.value)}>
    <option value="all">All stages</option>{stages.map(([id,label])=><option key={id} value={id}>{label}</option>)}</select></label>
   <span className="pv-total">{total} email{total===1?'':'s'} shown</span>
  </div>
  <p className="pv-assumption"><CalendarClock size={13} aria-hidden/> {view.assumption}</p>
  <div className="pv-days">{view.days.map(day=>{const list=shown(day);
   return <section className="pv-day" key={day.date} aria-labelledby={`pv-${day.date}`}>
    <header><h3 id={`pv-${day.date}`}>{weekday(day.date)}</h3>
     <p><strong>{list.length}{list.length!==day.count?` of ${day.count}`:''}</strong> email{list.length===1?'':'s'}
      <span>Daily cap {day.cap} per customer</span></p></header>
    {list.length===0?<p className="pv-empty">No planned emails{day.count?` match these filters (${day.count} planned)`:''}.</p>
     :<ul className="pv-list">{list.map(send=><li key={send.key} className="pv-item">
      <div className="pv-item-head"><strong>{send.customerName}</strong>
       <span className={send.autonomous?'pv-badge pv-amy':'pv-badge pv-person'}>
        {send.autonomous?<><ShieldCheck size={12} aria-hidden/> {agent} approves</>:<><UserCheck size={12} aria-hidden/> Needs a person</>}
       </span></div>
      <p className="pv-stage">{send.stageLabel} <span>· {clock(send.time)} · {send.channel}</span></p>
      <p className="pv-invoices">{send.invoiceNumbers.join(', ')}</p>
      <p className="pv-amounts">{send.amounts.length?send.amounts.map(money).join(' + '):'Amount from the policy step'}</p>
      {send.approval.requiresReview&&<p className="pv-review">Flagged for review before it can send.</p>}
      {send.blockers.length>0&&<ul className="pv-blockers" aria-label="Blockers">
       {send.blockers.map(b=><li key={`${b.code}:${b.subject.kind}:${b.subject.id}`}><strong>{blockerLabel(b.code)}</strong> {b.detail}</li>)}</ul>}
     </li>)}</ul>}
   </section>;})}</div>
 </section>;
}
